import { mdiBell, mdiCogOutline } from '@mdi/js'
import { Icon } from '@mdi/react'
import IconCheck from '@mui/icons-material/Check'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import { HelpToggleButton } from 'components/App/components/HelpToggleButton'
import PPSelect from 'components/PPSelect'
import { PPSelectOptions } from 'components/PPSelect/PPSelect'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  setCalendarMonthStart,
  setCalendarPostedVisible,
  setCalendarPostSize,
  setCalendarWeekStart,
  setPlannedPostsView,
  toggleEmptySlotsVisible,
  toggleVirtualPostsVisible
} from 'services/post'
import {
  calendarMonthStartSelector,
  calendarPostedVisibleSelector,
  calendarPostSizeSelector,
  calendarWeekStartSelector,
  emptySlotsVisibleSelector,
  virtualPostsVisibleSelector
} from 'services/post/selectors'
import { CalendarMonthStartOption, CalendarPostSize, PlannedPostsLayout } from 'services/post/state'
import { ContentType, POST_TYPES } from 'shared'
import { Weekday } from 'utils/calendar'
import { ActiveViewSelector } from '../ActiveViewSelector'
import PostsFilter from '../PostsFilter'
import styles from './CalendarViewOptions.pcss'

interface CalendarViewOptionsProps {
  postsCount: number | null
  withPreview: boolean
  showPendingLink?: boolean
  mode?: 'planned' | 'posted'
  className?: string
  activeView?: PlannedPostsLayout
  onActiveViewChange?: (value: PlannedPostsLayout) => void
  onTogglePreview?: (value: 'on' | 'off') => void
}

export function CalendarViewOptions({
  postsCount, withPreview, showPendingLink, mode = 'planned', className, activeView, onActiveViewChange, onTogglePreview
}: CalendarViewOptionsProps) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [popupAnchor, setPopupAnchor] = React.useState<HTMLElement | null>(null)
  const postSize = useSelector(calendarPostSizeSelector)
  const weekStart = useSelector(calendarWeekStartSelector)
  const monthStart = useSelector(calendarMonthStartSelector)
  const withEmptySlots = useSelector(emptySlotsVisibleSelector)
  const withPosted = useSelector(calendarPostedVisibleSelector)
  const virtualPostsVisible = useSelector(virtualPostsVisibleSelector)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const openPopup = (e: any) => {
    if (buttonRef.current) {
      setPopupAnchor(buttonRef.current)
    }
    e.stopPropagation()
    e.preventDefault()
  }

  const onToggleVirtualPostsVisible = React.useCallback(() => {
    dispatch(toggleVirtualPostsVisible())
  }, [dispatch])

  const closePopup = () => {
    setPopupAnchor(null)
  }

  const updatePostSize = (size: CalendarPostSize) => {
    dispatch(setCalendarPostSize(size))
  }

  const toggleEmptySlots = React.useCallback((value: 'on' | 'off') => {
    const visible = value === 'on'
    if (withEmptySlots !== visible) {
      dispatch(toggleEmptySlotsVisible())
    }
  }, [withEmptySlots, dispatch])

  const onMonthStartChange = (value: CalendarMonthStartOption) => {
    dispatch(setCalendarMonthStart(value))
  }

  const onWeekStartChange = (value: Weekday) => {
    dispatch(setCalendarWeekStart(value))
  }

  const onTogglePostedVisible = (value: 'on' | 'off') => {
    dispatch(setCalendarPostedVisible(value === 'on'))
  }

  const toggleOptions: PPSelectOptions = {
    on: { label: <FormattedMessage id="label.generic.on" /> },
    off: { label: <FormattedMessage id="label.generic.off" /> }
  }

  const onOffToggleOptions = [{
    label: <FormattedMessage id="label.generic.show" />,
    value: 'on'
  }, {
    label: <FormattedMessage id="label.generic.hide" />,
    value: 'off'
  }]

  const postSizeOptions = [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' }
  ]

  const goToPending = () => {
    navigate('/posts/planned/queued/pending')
  }

  const onFilter = (query: string) => {
    dispatch(setPlannedPostsView('list'))
    console.log(query)
    navigate(location.pathname, { state: { postsSearchQuery: query } })
  }

  return (
    <div className={`${styles.container} ${className || ''}`} data-testid="calendar-view-options">
      <div>
        <ActiveViewSelector
          className={styles['view-switch']}
          value={activeView}
          onChange={onActiveViewChange}
        />
        <PostsFilter
          activeTypes={POST_TYPES as ContentType[]}
          draftsView={false}
          // postsView={props.postsView}
          className={styles['search-container']}
          filterDisabled
          filterDisabledMessage="Please switch to List view to use post filters"
          onFilter={onFilter}
          // onPostViewChange={props.withPostsViewToggle && props.postsView ? onPostViewChange : undefined}
          // archivedPostsToggle={props.archivedPostsToggle}
        />
        <React.Fragment>
          <IconButton
            ref={buttonRef}
            size="small"
            className={`${styles['btn-action']} solid`}
            data-testid="btn-calendar-settings"
            onClick={openPopup}
          >
            <Icon size="20px" path={mdiCogOutline} />
          </IconButton>
          <Popper
            open={Boolean(popupAnchor)}
            anchorEl={popupAnchor}
            disablePortal
            placement="bottom"
            className={styles.popper}
          >
            <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={closePopup}>
              <Paper classes={{ root: styles.paper }}>
                {/** Empty slots toggle */}
                {mode === 'planned' && (
                  <React.Fragment>
                    <ViewOptionSelect
                      label={<FormattedMessage id="post.actions.toggle-empty-slots-calendar" />}
                      value={withEmptySlots ? 'on' : 'off'}
                      options={onOffToggleOptions}
                      onChange={toggleEmptySlots}
                    />

                    {/** Virtual posts toggle */}
                    <ViewOptionSelect
                      label={<FormattedMessage id="post.actions.toggle-virtual-posts" />}
                      value={virtualPostsVisible ? 'on' : 'off'}
                      options={onOffToggleOptions}
                      onChange={onToggleVirtualPostsVisible}
                    />

                    {/** History posts toggle */}
                    <ViewOptionSelect
                      label={<FormattedMessage id="post.calendar.labels.toggle-posted" />}
                      value={withPosted ? 'on' : 'off'}
                      options={onOffToggleOptions}
                      onChange={onTogglePostedVisible}
                    />
                  </React.Fragment>
                )}

                {/** Posts size */}
                <ViewOptionSelect
                  label={<FormattedMessage id="post.calendar.labels.post-size" />}
                  value={postSize}
                  options={postSizeOptions}
                  onChange={updatePostSize}
                />

                {/** Week start */}
                <ViewOptionSelect
                  label={<FormattedMessage id="post.actions.toggle-week-start" />}
                  value={weekStart}
                  options={[{
                    label: <FormattedMessage id="calendar.days.sunday" />,
                    value: 'sunday'
                  }, {
                    label: <FormattedMessage id="calendar.days.monday" />,
                    value: 'monday'
                  }]}
                  onChange={onWeekStartChange}
                />

                {/** Month start */}
                <ViewOptionSelect
                  label={<FormattedMessage id="post.actions.toggle-month-start" />}
                  value={monthStart}
                  options={[{
                    label: <FormattedMessage id="post.calendar.labels.this-week" />,
                    value: 'this-week'
                  }, {
                    label: <FormattedMessage id="post.calendar.labels.first" />,
                    value: '1st'
                  }]}
                  onChange={onMonthStartChange}
                />
              </Paper>
            </ClickAwayListener>
          </Popper>
        </React.Fragment>
        {showPendingLink && (
          <Button
            className={styles['btn-pending']}
            startIcon={<Icon path={mdiBell} size="20px" />}
            onClick={goToPending}
          >
            You have pending Queue posts
          </Button>
        )}
      </div>
      <div className={styles['header-right']}>
        {typeof postsCount === 'number' && (
          <div className={styles.count}>
            <FormattedMessage id="post.planned.posts-count-label" values={{ count: postsCount }} />
          </div>
        )}
        {onTogglePreview && (
          <PPSelect
            name={<FormattedMessage id="post.label.preview" />}
            options={toggleOptions}
            selectedValue={withPreview ? 'on' : 'off'}
            className={styles.select}
            onSelectionChange={onTogglePreview}
          />
        )}
        <HelpToggleButton />
      </div>
    </div>
  )
}

interface ViewOptionSelectProps {
  label: React.ReactNode
  options: Array<{ label: React.ReactNode, value: string, disabled?: boolean }>
  value: string
  onChange: (value: string) => void
}

export function ViewOptionSelect({ label, options, value, onChange }: ViewOptionSelectProps) {
  const onClick = (value: string, disabled?: boolean) => () => {
    if (!disabled) {
      onChange(value)
    }
  }

  return (
    <div className={styles.option} data-testid="view-option-select">
      <div className={styles.label}>
        {label}:
      </div>
      <div className={styles.switch}>
        {options.map(o => {
          const active = o.value === value
          return (
            <div
              key={o.value}
              className={`${active ? styles['tab-active'] : ''} ${o.disabled ? styles.disabled : ''}`}
              onClick={onClick(o.value, o.disabled)}
            >
              {o.label}
              {active && <IconCheck color="primary" fontSize="small" className={styles.icon} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default CalendarViewOptions
